// Simple Persian number to words conversion
const persianNumbers = {
  0: 'صفر',
  1: 'یک',
  2: 'دو',
  3: 'سه',
  4: 'چهار',
  5: 'پنج',
  6: 'شش',
  7: 'هفت',
  8: 'هشت',
  9: 'نه',
  10: 'ده',
  11: 'یازده',
  12: 'دوازده',
  13: 'سیزده',
  14: 'چهارده',
  15: 'پانزده',
  16: 'شانزده',
  17: 'هفده',
  18: 'هجده',
  19: 'نوزده',
  20: 'بیست',
  30: 'سی',
  40: 'چهل',
  50: 'پنجاه',
  60: 'شصت',
  70: 'هفتاد',
  80: 'هشتاد',
  90: 'نود',
  100: 'یکصد',
  200: 'دویست',
  300: 'سیصد',
  400: 'چهارصد',
  500: 'پانصد',
  600: 'ششصد',
  700: 'هفتصد',
  800: 'هشتصد',
  900: 'نهصد',
  1000: 'یک هزار',
  1000000: 'یک میلیون',
  1000000000: 'یک میلیارد'
};

const convertToPersianWordsSimple = (num: number): string => {
  if (num === 0) return 'صفر';
  if (num < 0) return 'منفی ' + convertToPersianWordsSimple(-num);
  
  if (num < 20) return persianNumbers[num as keyof typeof persianNumbers] || num.toString();
  
  if (num < 100) {
    const tens = Math.floor(num / 10) * 10;
    const ones = num % 10;
    if (ones === 0) return persianNumbers[tens as keyof typeof persianNumbers] || num.toString();
    return (persianNumbers[tens as keyof typeof persianNumbers] || tens.toString()) + ' و ' + (persianNumbers[ones as keyof typeof persianNumbers] || ones.toString());
  }
  
  if (num < 1000) {
    const hundreds = Math.floor(num / 100) * 100;
    const remainder = num % 100;
    if (remainder === 0) return persianNumbers[hundreds as keyof typeof persianNumbers] || num.toString();
    return (persianNumbers[hundreds as keyof typeof persianNumbers] || hundreds.toString()) + ' و ' + convertToPersianWordsSimple(remainder);
  }
  
  if (num < 1000000) {
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    const thousandsText = thousands === 1 ? 'یک هزار' : convertToPersianWordsSimple(thousands) + ' هزار';
    if (remainder === 0) return thousandsText;
    return thousandsText + ' و ' + convertToPersianWordsSimple(remainder);
  }
  
  if (num < 1000000000) {
    const millions = Math.floor(num / 1000000);
    const remainder = num % 1000000;
    const millionsText = millions === 1 ? 'یک میلیون' : convertToPersianWordsSimple(millions) + ' میلیون';
    if (remainder === 0) return millionsText;
    return millionsText + ' و ' + convertToPersianWordsSimple(remainder);
  }
  
  // For very large numbers, return a simple representation
  return num.toLocaleString('fa-IR') + ' (عدد بزرگ)';
};

/**
 * Format number with thousands separators (comma)
 * @param value - Number or string to format
 * @returns Formatted string with commas
 */
export const formatNumberWithCommas = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Convert to string and remove any existing commas
  const cleanValue = value.toString().replace(/,/g, '');
  
  // Check if it's a valid number
  if (isNaN(Number(cleanValue))) return value.toString();
  
  // Add commas for thousands separators
  return Number(cleanValue).toLocaleString('en-US');
};

/**
 * Convert number to Persian words using simple implementation
 * @param value - Number or string to convert
 * @returns Persian words representation
 */
export const convertToPersianWords = (value: string | number): string => {
  if (!value && value !== 0) return '';
  
  // Convert to string and remove any existing commas
  const cleanValue = value.toString().replace(/,/g, '');
  
  // Check if it's a valid number
  if (isNaN(Number(cleanValue))) return '';
  
  const numValue = Number(cleanValue);
  
  try {
    return convertToPersianWordsSimple(numValue);
  } catch (error) {

    return '';
  }
};

/**
 * Format number with commas and show Persian words
 * @param value - Number or string to format
 * @returns Object with formatted number and Persian words
 */
export const formatNumberWithWords = (value: string | number) => {
  const formatted = formatNumberWithCommas(value);
  const words = convertToPersianWords(value);
  
  return {
    formatted,
    words,
    display: words ? `${formatted} (${words})` : formatted
  };
};

/**
 * Parse formatted number back to clean number
 * @param formattedValue - Formatted string with commas
 * @returns Clean number string
 */
export const parseFormattedNumber = (formattedValue: string): string => {
  if (!formattedValue) return '';
  
  // Remove commas and return clean number
  return formattedValue.replace(/,/g, '');
};
